import { useMemo, useState } from "react";
import { Card, Input, Empty } from "./UI";
import { CocoaSelect } from "./CocoaSelect";
import { DoorOpen, BedDouble } from "lucide-react";

const HOUR_PX = 56;
const toMin = (t) => { if (!t) return null; const m = String(t).match(/(\d{1,2}):(\d{2})/); return m ? +m[1] * 60 + +m[2] : null; };
const fmtH = (h) => (h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`);

const typeStyle = (type = "") => {
  const t = type.toLowerCase();
  if (t.includes("group")) return { bg: "#E8F5E9", bd: "#2E7D32", tx: "#1B5E20" };
  if (t.includes("telehealth")) return { bg: "#EDE7F6", bd: "#5E35B1", tx: "#4527A0" };
  if (t.includes("intake") || t.includes("assess")) return { bg: "#E0F2F1", bd: "#00695C", tx: "#004D40" };
  if (t.includes("medication") || t.includes("med ")) return { bg: "#FFEBEE", bd: "#C62828", tx: "#B71C1C" };
  if (t.includes("family")) return { bg: "#FFF3E0", bd: "#EF6C00", tx: "#E65100" };
  return { bg: "#E3F2FD", bd: "#1565C0", tx: "#0D47A1" };
};

function lanes(items) {
  const sorted = [...items].sort((a, b) => (toMin(a.start_time) || 0) - (toMin(b.start_time) || 0));
  const ends = [];
  return sorted.map((a) => {
    const s = toMin(a.start_time) || 0;
    const e = toMin(a.end_time) || s + 60;
    let lane = ends.findIndex((x) => x <= s);
    if (lane === -1) { lane = ends.length; ends.push(e); } else ends[lane] = e;
    return { a, lane, total: 0 };
  }).map((x, _, arr) => ({ ...x, total: Math.max(...arr.map((y) => y.lane)) + 1 }));
}

function Block({ a, lane, total, startH, onAppt }) {
  const s = toMin(a.start_time) ?? startH * 60;
  const e = toMin(a.end_time) ?? s + 60;
  const top = ((s - startH * 60) / 60) * HOUR_PX;
  const height = Math.max(((e - s) / 60) * HOUR_PX, 22);
  const st = typeStyle(a.type);
  const w = 100 / total;
  const faded = ["missed", "no-show", "cancelled", "rescheduled"].includes(a.status);
  return (
    <button data-testid={`roomview-block-${a.id}`} onClick={(ev) => { ev.stopPropagation(); onAppt(a); }}
      style={{ top, height, left: `${lane * w}%`, width: `calc(${w}% - 3px)`, background: st.bg, borderLeft: `3px solid ${st.bd}`, color: st.tx }}
      className={`absolute z-10 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-[10px] leading-tight shadow-sm hover:shadow-md transition-shadow ${faded ? "opacity-50 line-through" : ""}`}>
      <span className="font-bold">{a.start_time}</span> {a.client_name}
      <div className="truncate opacity-80">{a.type}{a.bed ? ` · Bed ${a.bed}` : ""}</div>
    </button>
  );
}

function GhostBlock({ occ, startH }) {
  const s = toMin(occ.start_time) ?? startH * 60;
  const e = toMin(occ.end_time) ?? s + 60;
  const top = ((s - startH * 60) / 60) * HOUR_PX;
  const height = Math.max(((e - s) / 60) * HOUR_PX, 22);
  return (
    <div data-testid={`roomview-ghost-${occ.booking_id}`}
      style={{ top, height, left: 0, right: 0,
        backgroundImage: "repeating-linear-gradient(135deg, rgba(93,64,55,0.11) 0 6px, transparent 6px 12px)",
        borderLeft: "3px dashed #6D4C41" }}
      className="absolute z-0 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-[10px] leading-tight text-[#4E342E]">
      <span className="font-bold">Reserved</span> {occ.provider_name}
      <div className="truncate opacity-80">{occ.purpose || "Recurring block"}</div>
    </div>
  );
}

export function RoomScheduleView({ facilities, rooms, appts, occurrences = [], onSlot, onAppt }) {
  const [flt, setFlt] = useState({ facility: "", unit: "", floor: "", room: "", bed: "" });
  const f = (k) => (v) => setFlt((s) => ({ ...s, [k]: v, ...(k === "facility" ? { unit: "" } : {}) }));

  const units = useMemo(() => [...new Set(rooms.filter((r) => !flt.facility || r.facility_id === flt.facility).map((r) => r.unit).filter(Boolean))], [rooms, flt.facility]);
  const cols = useMemo(() => rooms.filter((r) =>
    (!flt.facility || r.facility_id === flt.facility) &&
    (!flt.unit || r.unit === flt.unit) &&
    (!flt.floor || String(r.floor || "").toLowerCase().includes(flt.floor.toLowerCase())) &&
    (!flt.room || (r.name || "").toLowerCase().includes(flt.room.toLowerCase())) &&
    (!flt.bed || (r.beds || []).some((b) => b.toLowerCase().includes(flt.bed.toLowerCase())))
  ), [rooms, flt]);

  const unassigned = useMemo(() => appts.filter((a) => !a.room_id), [appts]);
  const byRoom = useMemo(() => {
    const m = {};
    appts.forEach((a) => { if (a.room_id) (m[a.room_id] = m[a.room_id] || []).push(a); });
    return m;
  }, [appts]);
  const ghostByRoom = useMemo(() => {
    const m = {};
    occurrences.forEach((o) => { if (o.room_id) (m[o.room_id] = m[o.room_id] || []).push(o); });
    return m;
  }, [occurrences]);

  const [startH, endH] = useMemo(() => {
    let s = 7, e = 19;
    appts.forEach((a) => {
      const m = toMin(a.start_time); const me = toMin(a.end_time);
      if (m != null) s = Math.min(s, Math.floor(m / 60));
      if (me != null) e = Math.max(e, Math.ceil(me / 60));
    });
    occurrences.forEach((o) => {
      const m = toMin(o.start_time); const me = toMin(o.end_time);
      if (m != null) s = Math.min(s, Math.floor(m / 60));
      if (me != null) e = Math.max(e, Math.ceil(me / 60));
    });
    return [Math.max(0, s), Math.min(24, Math.max(e, s + 8))];
  }, [appts, occurrences]);
  const hours = Array.from({ length: endH - startH }, (_, i) => startH + i);
  const bodyH = hours.length * HOUR_PX;

  const column = (room, items, isUnassigned = false) => (
    <div key={isUnassigned ? "unassigned" : room.id} data-testid={isUnassigned ? "roomview-unassigned-col" : `roomview-room-col-${room.id}`}
      className="w-[176px] shrink-0 border-l border-[#EFEBE0] first:border-l-0">
      <div className={`h-[68px] border-b border-[#EFEBE0] px-2 py-1.5 ${isUnassigned ? "bg-[#F5F2EB]" : "bg-[#FDFCF8]"}`}>
        <div className="flex items-center gap-1 truncate text-xs font-bold text-[#3E2723]">
          <DoorOpen size={12} className="shrink-0 text-[#7B6157]" /> {isUnassigned ? "Unassigned" : room.name}
        </div>
        {!isUnassigned && (
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[9px] text-[#7B6157]">
            {room.unit && <span className="rounded bg-[#EFEBE0] px-1 py-px font-semibold">{room.unit}</span>}
            {room.floor && <span className="rounded bg-[#EFEBE0] px-1 py-px font-semibold">Fl {room.floor}</span>}
            {(room.beds || []).length > 0 && <span className="flex items-center gap-0.5"><BedDouble size={9} /> {room.beds.length}</span>}
            <span className="truncate">{room.room_type}</span>
          </div>
        )}
        {isUnassigned && <div className="mt-0.5 text-[9px] text-[#84675B]">No room assigned yet</div>}
        <div className="text-[9px] font-semibold text-[#6D4C41]">
          {items.length} booking{items.length === 1 ? "" : "s"}
          {!isUnassigned && (ghostByRoom[room.id] || []).length > 0 && (
            <span className="ml-1 text-[#8D5A00]">· {(ghostByRoom[room.id] || []).length} held</span>
          )}
        </div>
      </div>
      <div className="relative" style={{ height: bodyH }}>
        {hours.map((h) => (
          <div key={h} data-testid={!isUnassigned ? `roomview-slot-${room.id}-${h}` : undefined}
            onClick={() => !isUnassigned && onSlot(room, h)}
            style={{ top: (h - startH) * HOUR_PX, height: HOUR_PX }}
            className={`absolute inset-x-0 border-b border-dashed border-[#EFEBE0] ${isUnassigned ? "" : "cursor-pointer hover:bg-[#F5F2EB]/60"}`} />
        ))}
        {!isUnassigned && (ghostByRoom[room.id] || []).map((occ, i) => (
          <GhostBlock key={`${occ.booking_id}-${i}`} occ={occ} startH={startH} />
        ))}
        {lanes(items).map(({ a, lane, total }) => <Block key={a.id} a={a} lane={lane} total={total} startH={startH} onAppt={onAppt} />)}
      </div>
    </div>
  );

  return (
    <Card className="p-4" data-testid="roomview-grid">
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#EFEBE0] bg-[#F5F2EB]/60 p-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#5D4037]">Facility</span>
          <CocoaSelect testId="roomview-facility-select" className="w-56" placeholder="All facilities"
            value={flt.facility} onChange={f("facility")} options={facilities.map((x) => [x.id, x.name])} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#5D4037]">Unit</span>
          <CocoaSelect testId="roomview-unit-select" className="w-36" placeholder="All units"
            value={flt.unit} onChange={f("unit")} options={units} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#5D4037]">Floor</span>
          <Input data-testid="roomview-floor-input" className="w-24" placeholder="Enter floor" value={flt.floor} onChange={(e) => f("floor")(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#5D4037]">Room</span>
          <Input data-testid="roomview-room-input" className="w-32" placeholder="Enter room" value={flt.room} onChange={(e) => f("room")(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#5D4037]">Bed</span>
          <Input data-testid="roomview-bed-input" className="w-28" placeholder="Enter bed" value={flt.bed} onChange={(e) => f("bed")(e.target.value)} />
        </div>
      </div>
      {cols.length === 0 && unassigned.length === 0 ? (
        <Empty text="No rooms match these filters. Add rooms under Settings, Facilities & Rooms." />
      ) : (
        <div className="overflow-x-auto">
          <div className="flex min-w-max">
            <div className="w-14 shrink-0">
              <div className="h-[68px] border-b border-[#EFEBE0]" />
              <div className="relative" style={{ height: bodyH }}>
                {hours.map((h) => (
                  <div key={h} style={{ top: (h - startH) * HOUR_PX }} className="absolute right-2 -translate-y-1/2 text-[10px] font-semibold text-[#84675B]">{fmtH(h)}</div>
                ))}
              </div>
            </div>
            {unassigned.length > 0 && column({ id: "none" }, unassigned, true)}
            {cols.map((room) => column(room, byRoom[room.id] || []))}
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-[#6D4C41]">
        {[["Individual", "#1565C0"], ["Group", "#2E7D32"], ["Telehealth", "#5E35B1"], ["Intake", "#00695C"], ["Medication", "#C62828"], ["Family", "#EF6C00"]].map(([l, c]) => (
          <span key={l} className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: c }} /> {l}</span>
        ))}
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ backgroundImage: "repeating-linear-gradient(135deg, #6D4C41 0 2px, transparent 2px 4px)" }} /> Recurring block</span>
        <span className="ml-auto text-[#84675B]">Click an empty slot to book that room</span>
      </div>
    </Card>
  );
}
